// Centro de presión y fuerza normal por el método de Barrowman. Ver docs/theory/barrowman.md.
import { attachedFront, bodyRadiusAt, layoutStack, profileVolume, noseRadius, referenceDiameter, type Layout } from '../rocket/geometry';
import type { FinSet, Rocket } from '../rocket/types';
import { DEFAULT_STALL_ANGLE, FIN_MACH_CAP } from './flight-conditions';

const noseVolumeCache = new WeakMap<object, number>();
function noseVolume(nose: Parameters<typeof noseRadius>[0]): number {
  let v = noseVolumeCache.get(nose);
  if (v === undefined) {
    v = profileVolume((x) => noseRadius(nose, x), nose.length);
    noseVolumeCache.set(nose, v);
  }
  return v;
}

export interface ComponentNormalForce {
  readonly id: string;
  readonly type: 'nose-cone' | 'transition' | 'fin-set';
  /** Pendiente de la fuerza normal por radián, referida a A_ref. */
  readonly cnAlpha: number;
  /** Posición del CP de la contribución desde la punta (m). */
  readonly cpX: number;
}

export interface NormalForceOptions {
  /** Número de Mach (solo afecta a las aletas). Por defecto 0. */
  readonly mach?: number;
}

/** Posición del CP de una aleta trapezoidal desde el borde de ataque de la raíz. */
export function finCenterOfPressure(f: Pick<FinSet, 'rootChord' | 'tipChord' | 'sweep'>): number {
  const sum = f.rootChord + f.tipChord;
  return (f.sweep / 3) * ((f.rootChord + 2 * f.tipChord) / sum) + (1 / 6) * (sum - (f.rootChord * f.tipChord) / sum);
}

/** Forma cerrada de Barrowman (subsónica incompresible) para el juego de aletas, sin interferencia. */
export function finCnAlphaBarrowman(f: FinSet, refDiameter: number): number {
  const lm = Math.hypot(f.span, f.sweep + (f.tipChord - f.rootChord) / 2);
  const root = Math.sqrt(1 + ((2 * lm) / (f.rootChord + f.tipChord)) ** 2);
  return (4 * f.finCount * (f.span / refDiameter) ** 2) / (1 + root);
}

/** Pendiente de la fuerza normal del juego de aletas con compresibilidad e interferencia. */
export function finCnAlpha(f: FinSet, refDiameter: number, bodyRadius: number, mach: number): number {
  const m = Math.min(Math.max(mach, 0), FIN_MACH_CAP);
  const beta2 = 1 - m * m;
  const planform = ((f.rootChord + f.tipChord) * f.span) / 2;
  const aspectPair = (4 * f.span) / (f.rootChord + f.tipChord);
  const tanMid = (f.sweep + (f.tipChord - f.rootChord) / 2) / f.span;
  const clAlphaPair = (2 * Math.PI * aspectPair) / (2 + Math.sqrt(4 + aspectPair ** 2 * (beta2 + tanMid ** 2)));
  const refArea = (Math.PI * refDiameter ** 2) / 4;
  const interference = 1 + bodyRadius / (f.span + bodyRadius);
  return interference * (f.finCount / 2) * clAlphaPair * (planform / refArea);
}

function transitionContribution(entry: Layout, refRadius: number): ComponentNormalForce | undefined {
  const c = entry.component;
  if (c.type !== 'transition') return undefined;
  const rf = c.foreDiameter / 2;
  const ra = c.aftDiameter / 2;
  if (rf === ra) return undefined;
  const r = rf / ra;
  return {
    id: c.id,
    type: 'transition',
    cnAlpha: 2 * ((ra / refRadius) ** 2 - (rf / refRadius) ** 2),
    cpX: entry.x0 + (c.length / 3) * (1 + 1 / (1 + r)),
  };
}

/** Contribuciones de fuerza normal de cada componente (cono, transiciones y aletas). */
export function componentNormalForces(rocket: Rocket, opts: NormalForceOptions = {}): ComponentNormalForce[] {
  const d = referenceDiameter(rocket);
  const refRadius = d / 2;
  const out: ComponentNormalForce[] = [];
  for (const entry of layoutStack(rocket)) {
    const c = entry.component;
    if (c.type === 'nose-cone') {
      const rn = c.baseDiameter / 2;
      const volume = noseVolume(c);
      out.push({ id: c.id, type: 'nose-cone', cnAlpha: 2 * (rn / refRadius) ** 2, cpX: entry.x0 + c.length - volume / (Math.PI * rn * rn) });
      continue;
    }
    const t = transitionContribution(entry, refRadius);
    if (t !== undefined) out.push(t);
    for (const child of c.children) {
      if (child.type !== 'fin-set') continue;
      const front = attachedFront(entry, child.rootChord, child.position);
      const rb = bodyRadiusAt(entry, front + child.rootChord / 2);
      out.push({ id: child.id, type: 'fin-set', cnAlpha: finCnAlpha(child, d, rb, opts.mach ?? 0), cpX: front + finCenterOfPressure(child) });
    }
  }
  return out;
}

export interface CenterOfPressure {
  /** Suma de las pendientes de fuerza normal (por radián, referida a A_ref). */
  readonly cnAlpha: number;
  /** CP desde la punta (m); NaN si la pendiente total es cero. */
  readonly cpX: number;
  readonly components: readonly ComponentNormalForce[];
}

export function centerOfPressure(rocket: Rocket, opts: NormalForceOptions = {}): CenterOfPressure {
  const components = componentNormalForces(rocket, opts);
  const cnAlpha = components.reduce((s, c) => s + c.cnAlpha, 0);
  const cpX = cnAlpha === 0 ? Number.NaN : components.reduce((s, c) => s + c.cnAlpha * c.cpX, 0) / cnAlpha;
  return { cnAlpha, cpX, components };
}

export interface StabilityOptions extends NormalForceOptions {
  /** Margen mínimo recomendado (calibres). Por defecto 1. */
  readonly minMargin?: number;
  /** Margen máximo recomendado (calibres). Por defecto 6. */
  readonly maxMargin?: number;
}

export interface Stability extends CenterOfPressure {
  readonly cgX: number;
  /** (x_CP − x_CG)/d, en calibres. Positivo si el CP está detrás del CG. */
  readonly margin: number;
  readonly warnings: readonly string[];
}

export function stability(rocket: Rocket, cgX: number, opts: StabilityOptions = {}): Stability {
  const cp = centerOfPressure(rocket, opts);
  const d = referenceDiameter(rocket);
  const margin = (cp.cpX - cgX) / d;
  const warnings: string[] = [];
  const min = opts.minMargin ?? 1;
  const max = opts.maxMargin ?? 6;
  if (Number.isNaN(margin)) warnings.push('No hay superficies que generen fuerza normal: el cohete no tiene estabilidad aerodinámica');
  else if (margin < 0) warnings.push('El cohete es inestable: el CP está por delante del CG');
  else if (margin < min) warnings.push(`El cohete tiene estabilidad marginal: ${margin.toFixed(2)} calibres (menos de ${min})`);
  else if (margin > max) warnings.push(`El cohete es demasiado estable: ${margin.toFixed(2)} calibres (más de ${max})`);
  return { ...cp, cgX, margin, warnings };
}

export interface NormalForce {
  readonly cn: number;
  readonly stalled: boolean;
}

/** Coeficiente de fuerza normal, lineal hasta el ángulo de pérdida y constante por encima. */
export function normalForceCoefficient(cnAlpha: number, angleOfAttack: number, stallAngle: number = DEFAULT_STALL_ANGLE): NormalForce {
  const a = Math.abs(angleOfAttack);
  const stalled = a > stallAngle;
  return { cn: cnAlpha * Math.sign(angleOfAttack) * Math.min(a, stallAngle), stalled };
}
